import { useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Trash2, ExternalLink, Pause, Play, ChevronRight, ChevronLeft, Check, ArrowRight, Loader2 } from "lucide-react";

const INDUSTRIES = [
  { id: "garage_door", label: "Garage Door" },
  { id: "striping", label: "Parking Lot Striping" },
  { id: "roofing", label: "Roofing" },
  { id: "hvac", label: "HVAC" },
  { id: "plumbing", label: "Plumbing" },
  { id: "electrical", label: "Electrical" },
  { id: "concrete", label: "Concrete" },
  { id: "landscaping", label: "Landscaping" },
  { id: "pharmacy", label: "Pharmacy" },
  { id: "medical", label: "Medical / Healthcare" },
  { id: "legal", label: "Legal" },
  { id: "beauty", label: "Beauty / Wellness" },
  { id: "trades", label: "General Trades" },
  { id: "general_service", label: "General Service Business" },
  { id: "professional_services", label: "Professional Services" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "hospitality", label: "Hospitality" },
  { id: "other", label: "Other" },
];

const TIME_ZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  "Europe/London", "Europe/Paris", "Asia/Tokyo", "Australia/Sydney",
];

const initialDraft = () => ({
  name: "",
  slug: "",
  domain: "",
  logo_url: "",
  primary_color: "#0055FF",
  secondary_color: "#121212",
  tagline: "",
  description: "",
  phone: "",
  email: "",
  address: "",
  time_zone: "America/New_York",
  industry: "other",
  status: "active",
});

export default function Tenants() {
  const { user } = useAuth();
  const { tenants, refreshTenants, switchTenant } = useTenant();
  const [showWizard, setShowWizard] = useState(false);
  const [editing, setEditing] = useState(null);

  const create = async (draft) => {
    const { data } = await api.post("/tenants", draft);
    await refreshTenants();
    return data;
  };
  const save = async () => {
    const { id, slug, ...rest } = editing;
    await api.patch(`/tenants/${id}`, rest);
    setEditing(null);
    refreshTenants();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this tenant and all of its data? This cannot be undone.")) return;
    await api.delete(`/tenants/${id}`);
    refreshTenants();
  };
  const toggleStatus = async (t) => {
    const next = t.status === "paused" ? "active" : "paused";
    await api.patch(`/tenants/${t.id}`, { status: next });
    refreshTenants();
  };

  const isPlatformOwner = ["platform_owner", "super_admin"].includes(user?.role);

  return (
    <div className="p-8 max-w-6xl" data-testid="tenants-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="n-label">Brands managed by Nariai</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Tenants</h1>
        </div>
        {isPlatformOwner && (
          <button className="n-btn flex items-center gap-2" onClick={() => setShowWizard(true)} data-testid="new-tenant-button">
            <Plus size={14} /> New Tenant
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {tenants.map((t) => (
          <div key={t.id} className="n-card p-5" data-testid={`tenant-card-${t.slug}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ background: t.primary_color }} />
                  <div className="font-cab font-black text-2xl tracking-tight">{t.name}</div>
                  <span
                    className={`n-chip ${t.status === "active" ? "text-[var(--n-success)]" : t.status === "paused" ? "text-[var(--n-warning)]" : t.status === "suspended" ? "text-[var(--n-danger)]" : ""}`}
                    data-testid={`tenant-status-${t.slug}`}
                  >
                    {t.status || "active"}
                  </span>
                </div>
                <div className="text-xs font-mono-plex text-[var(--n-text-muted)] mt-1">
                  /{t.slug} {t.domain && `· ${t.domain}`} {t.industry && `· ${t.industry}`}
                </div>
                <div className="text-sm text-[var(--n-text-sec)] mt-3 line-clamp-2">{t.description}</div>
              </div>
              <a href={`/site/${t.slug}`} target="_blank" rel="noreferrer" className="text-[var(--n-text-sec)] hover:text-white" data-testid={`tenant-visit-${t.slug}`}>
                <ExternalLink size={16} />
              </a>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <button onClick={() => switchTenant(t.id)} className="n-btn-ghost n-btn text-xs" data-testid={`tenant-activate-${t.slug}`}>
                ACTIVATE
              </button>
              <button onClick={() => setEditing({ ...t })} className="n-btn-ghost n-btn text-xs" data-testid={`tenant-edit-${t.slug}`}>
                EDIT
              </button>
              {isPlatformOwner && (
                <>
                  <button
                    onClick={() => toggleStatus(t)}
                    className="n-btn-ghost n-btn text-xs flex items-center gap-1"
                    data-testid={`tenant-toggle-${t.slug}`}
                  >
                    {t.status === "paused" ? <><Play size={11}/>RESUME</> : <><Pause size={11}/>PAUSE</>}
                  </button>
                  <button onClick={() => remove(t.id)} className="ml-auto text-[var(--n-danger)] text-xs flex items-center gap-1" data-testid={`tenant-delete-${t.slug}`}>
                    <Trash2 size={12} /> delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {showWizard && <TenantWizard onCancel={() => setShowWizard(false)} onCreate={create} />}
      {editing && (
        <Modal title={`Edit · ${editing.name}`} onClose={() => setEditing(null)}>
          <TenantForm draft={editing} setDraft={setEditing} onSubmit={(e) => { e.preventDefault(); save(); }} submitLabel="SAVE CHANGES" isEdit />
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="n-card w-full max-w-2xl p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-cab font-black text-xl">{title}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// -------- Wizard --------
const PROVISION_STEPS = [
  { key: "website", label: "Creating website" },
  { key: "crm", label: "Creating CRM" },
  { key: "ai", label: "Configuring AI" },
  { key: "seo", label: "Provisioning SEO" },
  { key: "forms", label: "Building forms" },
  { key: "analytics", label: "Setting up analytics" },
  { key: "homepage", label: "Generating homepage" },
  { key: "ready", label: "Business ready" },
];

function TenantWizard({ onCancel, onCreate }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(initialDraft());
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("form"); // form | provisioning | success | error
  const [progressIdx, setProgressIdx] = useState(0);
  const [createdTenant, setCreatedTenant] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const steps = ["Business", "Domain", "Industry", "Brand", "Review"];

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setBusy(true);
    setPhase("provisioning");
    setProgressIdx(0);
    // Start the visible progress animation in parallel with the API call
    const stepDelay = 350; // ms between check-ins
    const timer = setInterval(() => {
      setProgressIdx((p) => Math.min(p + 1, PROVISION_STEPS.length - 2));
    }, stepDelay);
    try {
      const tenant = await onCreate(draft);
      // Finish the animation
      clearInterval(timer);
      setProgressIdx(PROVISION_STEPS.length - 1);
      setCreatedTenant(tenant);
      await new Promise((r) => setTimeout(r, 500));
      setPhase("success");
    } catch (e) {
      clearInterval(timer);
      setErrorMsg(e.response?.data?.detail || "Failed to create tenant");
      setPhase("error");
    } finally {
      setBusy(false);
    }
  };

  const canAdvance = () => {
    if (step === 0) return draft.name.trim() && draft.slug.trim();
    if (step === 1) return true;
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="tenant-wizard">
      <div className="n-card w-full max-w-2xl p-8 max-h-[90vh] overflow-auto">
        {phase === "provisioning" && (
          <ProvisioningScreen currentIdx={progressIdx} name={draft.name} />
        )}

        {phase === "success" && createdTenant && (
          <SuccessScreen tenant={createdTenant} onClose={onCancel} />
        )}

        {phase === "error" && (
          <div className="text-center py-10" data-testid="wizard-error">
            <div className="text-[var(--n-danger)] text-6xl mb-4">×</div>
            <div className="font-cab font-black text-2xl mb-2">Something went wrong</div>
            <div className="text-sm text-[var(--n-text-sec)] mb-6">{errorMsg}</div>
            <button className="n-btn" onClick={() => setPhase("form")} data-testid="wizard-retry">Back to review</button>
          </div>
        )}

        {phase === "form" && (
        <>
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="n-label">Onboard a new brand</div>
            <div className="font-cab font-black text-3xl tracking-tight mt-1">Create tenant</div>
          </div>
          <button onClick={onCancel} data-testid="wizard-close"><X size={18} /></button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono-plex ${
                  i === step ? "bg-white text-black" : i < step ? "bg-[var(--n-success)] text-black" : "bg-[var(--n-surface)] text-[var(--n-text-muted)] border border-[var(--n-border)]"
                }`}
              >
                {i + 1}
              </div>
              <span className={`text-xs font-mono-plex uppercase ${i === step ? "text-white" : "text-[var(--n-text-muted)]"}`}>{s}</span>
              {i < steps.length - 1 && <div className="flex-1 h-px bg-[var(--n-border)]" />}
            </div>
          ))}
        </div>

        {/* Steps */}
        {step === 0 && (
          <div className="space-y-4" data-testid="wizard-step-business">
            <Field label="Business name" required>
              <input className="n-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") })} placeholder="e.g. Lone Star Striping Pros" data-testid="wizard-name" />
            </Field>
            <Field label="Slug" hint="URL-safe identifier. Auto-generated from name; edit if needed." required>
              <input className="n-input font-mono-plex" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} data-testid="wizard-slug" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><input className="n-input" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} data-testid="wizard-phone" /></Field>
              <Field label="Email"><input className="n-input" type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="wizard-email" /></Field>
            </div>
            <Field label="Address"><input className="n-input" value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} data-testid="wizard-address" /></Field>
            <Field label="Time zone">
              <select className="n-input" value={draft.time_zone} onChange={(e) => setDraft({ ...draft, time_zone: e.target.value })} data-testid="wizard-timezone">
                {TIME_ZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-domain">
            <Field label="Primary domain" hint="e.g. yourdomain.com — this is used for domain-based tenant routing.">
              <input className="n-input font-mono-plex" value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.target.value.toLowerCase().trim() })} placeholder="acme.com" data-testid="wizard-domain" />
            </Field>
            <div className="border border-dashed border-[var(--n-border)] p-4 text-xs font-mono-plex text-[var(--n-text-muted)]">
              DNS: point <span className="text-white">{draft.domain || "yourdomain.com"}</span> at Nariai (A/CNAME) once ready. Until then the site is reachable at <span className="text-white">/site/{draft.slug || "your-slug"}</span>.
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-industry">
            <Field label="Industry" hint="Determines the homepage template, services, FAQs, AI tone, and SMS templates.">
              <div className="grid grid-cols-2 gap-2 max-h-[320px] overflow-auto pr-1">
                {INDUSTRIES.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => setDraft({ ...draft, industry: i.id })}
                    className={`text-left p-3 border ${draft.industry === i.id ? "border-white bg-[#101015]" : "border-[var(--n-border)] hover:bg-[#0c0c10]"}`}
                    data-testid={`wizard-industry-${i.id}`}
                  >
                    <div className="font-cab font-bold text-sm">{i.label}</div>
                    <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)] mt-1">{i.id}</div>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Tagline"><input className="n-input" value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} placeholder="Short one-liner" data-testid="wizard-tagline" /></Field>
            <Field label="Description"><textarea rows={3} className="n-input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="wizard-description" /></Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-brand">
            <Field label="Logo URL" hint="HTTPS URL to your logo. Upload support coming in Phase 2.">
              <input className="n-input" value={draft.logo_url} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} data-testid="wizard-logo" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Primary color">
                <div className="flex gap-2 items-center">
                  <input type="color" value={draft.primary_color} onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })} className="w-12 h-12 bg-transparent border border-[var(--n-border)]" data-testid="wizard-primary-color" />
                  <input className="n-input font-mono-plex text-xs" value={draft.primary_color} onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })} />
                </div>
              </Field>
              <Field label="Secondary color">
                <div className="flex gap-2 items-center">
                  <input type="color" value={draft.secondary_color} onChange={(e) => setDraft({ ...draft, secondary_color: e.target.value })} className="w-12 h-12 bg-transparent border border-[var(--n-border)]" data-testid="wizard-secondary-color" />
                  <input className="n-input font-mono-plex text-xs" value={draft.secondary_color} onChange={(e) => setDraft({ ...draft, secondary_color: e.target.value })} />
                </div>
              </Field>
            </div>
            <div className="border border-[var(--n-border)] p-4">
              <div className="n-label mb-2">Preview</div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center font-cab font-black text-2xl" style={{ background: draft.primary_color, color: "#fff" }}>
                  {draft.name?.[0] || "•"}
                </div>
                <div>
                  <div className="font-cab font-black text-xl">{draft.name || "Your brand"}</div>
                  <div className="text-xs text-[var(--n-text-sec)]">{draft.tagline || "Tagline goes here"}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="wizard-step-review">
            <div className="n-label mb-2">Review & create</div>
            <div className="border border-[var(--n-border)] p-4 space-y-2 text-sm font-mono-plex">
              {[
                ["name", draft.name], ["slug", draft.slug], ["domain", draft.domain || "(none)"],
                ["industry", draft.industry], ["time_zone", draft.time_zone], ["status", draft.status],
                ["phone", draft.phone || "—"], ["email", draft.email || "—"],
                ["primary", draft.primary_color], ["secondary", draft.secondary_color],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 text-xs">
                  <span className="text-[var(--n-text-muted)]">{k}</span>
                  <span className="text-white">{v}</span>
                </div>
              ))}
            </div>
            <div className="text-xs text-[var(--n-text-muted)] font-mono-plex">
              On create, we will auto-provision: AI settings (industry-tuned), default SEO page, email & SMS templates.
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="n-btn-ghost n-btn flex items-center gap-1 disabled:opacity-30"
            data-testid="wizard-back"
          >
            <ChevronLeft size={14}/> Back
          </button>

          {step < steps.length - 1 ? (
            <button
              type="button"
              onClick={next}
              disabled={!canAdvance()}
              className="n-btn flex items-center gap-1 disabled:opacity-40"
              data-testid="wizard-next"
            >
              Next <ChevronRight size={14}/>
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="n-btn-blue n-btn disabled:opacity-40"
              data-testid="wizard-create"
            >
              {busy ? "CREATING…" : "CREATE TENANT"}
            </button>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// -------- Provisioning progress screen --------
function ProvisioningScreen({ currentIdx, name }) {
  return (
    <div className="py-6" data-testid="provisioning-screen">
      <div className="text-center mb-8">
        <div className="n-label">Building your business</div>
        <div className="font-cab font-black text-3xl tracking-tight mt-1">{name || "New tenant"}</div>
        <div className="text-xs text-[var(--n-text-sec)] mt-2 font-mono-plex">This usually takes ~10 seconds.</div>
      </div>
      <div className="space-y-2 max-w-md mx-auto">
        {PROVISION_STEPS.map((s, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          const pending = i > currentIdx;
          return (
            <div
              key={s.key}
              className={`flex items-center gap-3 px-4 py-3 border rounded ${
                done ? "border-[var(--n-success)] bg-[var(--n-surface)]" :
                active ? "border-white bg-[#0f0f14]" :
                "border-[var(--n-border)] opacity-60"
              }`}
              data-testid={`provision-step-${s.key}`}
              data-status={done ? "done" : active ? "active" : "pending"}
            >
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                {done && <Check size={16} className="text-[var(--n-success)]" />}
                {active && <Loader2 size={16} className="animate-spin text-white" />}
                {pending && <div className="w-2 h-2 rounded-full bg-[var(--n-border)]" />}
              </div>
              <div className={`font-cab font-medium text-sm ${done ? "text-[var(--n-text-sec)]" : active ? "text-white" : "text-[var(--n-text-muted)]"}`}>
                {s.label}{active ? "…" : done ? "" : ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------- Success screen with next-step CTAs --------
function SuccessScreen({ tenant, onClose }) {
  return (
    <div className="py-6" data-testid="success-screen">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[var(--n-success)]/15 flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-[var(--n-success)]" strokeWidth={2.5} />
        </div>
        <div className="n-label" style={{ color: "var(--n-success)" }}>Business ready</div>
        <div className="font-cab font-black text-4xl tracking-tight mt-1">{tenant.name}</div>
        <div className="text-sm text-[var(--n-text-sec)] mt-3 max-w-md mx-auto">
          Your complete business operating system is live. Website, CRM, AI, SEO, forms, and analytics — all provisioned and ready to use.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        <a
          href={`/site/${tenant.slug}`}
          target="_blank"
          rel="noreferrer"
          className="border border-[var(--n-border)] rounded p-4 hover:bg-[#0f0f14] transition-colors flex flex-col gap-1"
          data-testid="success-preview-website"
        >
          <div className="flex items-center gap-2 text-white">
            <ExternalLink size={14} />
            <span className="font-cab font-bold text-sm">Preview website</span>
          </div>
          <div className="text-[10px] text-[var(--n-text-muted)] font-mono-plex">See your live homepage</div>
        </a>

        <button
          onClick={() => { window.location.href = "/admin"; }}
          className="border border-[var(--n-border)] rounded p-4 hover:bg-[#0f0f14] transition-colors flex flex-col gap-1 text-left"
          data-testid="success-open-dashboard"
        >
          <div className="flex items-center gap-2 text-white">
            <ArrowRight size={14} />
            <span className="font-cab font-bold text-sm">Open dashboard</span>
          </div>
          <div className="text-[10px] text-[var(--n-text-muted)] font-mono-plex">Manage the business</div>
        </button>

        <button
          onClick={() => { window.location.href = "/admin/website/pages"; }}
          className="border border-[var(--n-border)] rounded p-4 hover:bg-[#0f0f14] transition-colors flex flex-col gap-1 text-left"
          data-testid="success-edit-website"
        >
          <div className="flex items-center gap-2 text-white">
            <ArrowRight size={14} />
            <span className="font-cab font-bold text-sm">Edit website</span>
          </div>
          <div className="text-[10px] text-[var(--n-text-muted)] font-mono-plex">Customize blocks & copy</div>
        </button>

        <button
          onClick={() => { window.location.href = "/admin/tenants"; onClose(); }}
          className="border border-white bg-white text-black rounded p-4 hover:bg-[#f0f0f0] transition-colors flex flex-col gap-1 text-left"
          data-testid="success-connect-domain"
        >
          <div className="flex items-center gap-2">
            <ArrowRight size={14} />
            <span className="font-cab font-bold text-sm">Connect domain</span>
          </div>
          <div className="text-[10px] text-black/60 font-mono-plex">Point DNS to Nariai</div>
        </button>
      </div>

      <div className="mt-8 max-w-md mx-auto border border-[var(--n-border)] rounded p-4">
        <div className="n-label mb-3">Provisioned for you</div>
        <div className="grid grid-cols-2 gap-2 text-xs font-mono-plex">
          {["Complete Website","Homepage","CRM","AI Configuration","SEO Foundation","Analytics","Forms","Media Library","Dashboard","Public Website"].map((f) => (
            <div key={f} className="flex items-center gap-2 text-[var(--n-text-sec)]">
              <Check size={12} className="text-[var(--n-success)]" strokeWidth={2.5} /> {f}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -------- Edit form (reused for edit modal) --------
function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="n-label block mb-1">{label}{required && <span className="text-[var(--n-danger)] ml-1">*</span>}</label>
      {children}
      {hint && <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)] mt-1">{hint}</div>}
    </div>
  );
}

function TenantForm({ draft, setDraft, onSubmit, submitLabel, isEdit }) {
  return (
    <form onSubmit={onSubmit} data-testid="tenant-form" className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" required><input className="n-input" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required data-testid="tenant-field-name" /></Field>
        {!isEdit && <Field label="Slug" required><input className="n-input font-mono-plex" value={draft.slug || ""} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} required data-testid="tenant-field-slug" /></Field>}
        <Field label="Domain"><input className="n-input font-mono-plex" value={draft.domain || ""} onChange={(e) => setDraft({ ...draft, domain: e.target.value })} data-testid="tenant-field-domain" /></Field>
        <Field label="Industry">
          <select className="n-input" value={draft.industry || "other"} onChange={(e) => setDraft({ ...draft, industry: e.target.value })} data-testid="tenant-field-industry">
            {INDUSTRIES.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select className="n-input" value={draft.status || "active"} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="tenant-field-status">
            {["active", "trial", "paused", "suspended"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Time zone">
          <select className="n-input" value={draft.time_zone || "America/New_York"} onChange={(e) => setDraft({ ...draft, time_zone: e.target.value })}>
            {TIME_ZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Field>
        <Field label="Phone"><input className="n-input" value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></Field>
        <Field label="Email"><input className="n-input" value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field>
        <Field label="Logo URL"><input className="n-input" value={draft.logo_url || ""} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} data-testid="tenant-field-logo_url" /></Field>
        <Field label="Tagline"><input className="n-input" value={draft.tagline || ""} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} /></Field>
      </div>
      <Field label="Address"><input className="n-input" value={draft.address || ""} onChange={(e) => setDraft({ ...draft, address: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Primary color">
          <div className="flex items-center gap-2">
            <input type="color" value={draft.primary_color || "#0055FF"} onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })} className="w-10 h-10 bg-transparent border border-[var(--n-border)]" />
            <input className="n-input font-mono-plex text-xs" value={draft.primary_color || ""} onChange={(e) => setDraft({ ...draft, primary_color: e.target.value })} />
          </div>
        </Field>
        <Field label="Secondary color">
          <div className="flex items-center gap-2">
            <input type="color" value={draft.secondary_color || "#121212"} onChange={(e) => setDraft({ ...draft, secondary_color: e.target.value })} className="w-10 h-10 bg-transparent border border-[var(--n-border)]" />
            <input className="n-input font-mono-plex text-xs" value={draft.secondary_color || ""} onChange={(e) => setDraft({ ...draft, secondary_color: e.target.value })} />
          </div>
        </Field>
      </div>
      <Field label="Description"><textarea rows={3} className="n-input" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
      <button className="n-btn w-full mt-2" type="submit" data-testid="tenant-form-submit">{submitLabel}</button>
    </form>
  );
}
